interface BoxProps {
  x: number;
  y: number;
  w: number;
  h: number;
  title: string;
  lines?: readonly string[];
  variant?: 'core' | 'note';
}

const Box = ({ x, y, w, h, title, lines = [], variant }: BoxProps) => {
  const cx = x + w / 2;
  // Title then sub-lines, vertically centred as a block inside the box.
  const blockHeight = 16 + lines.length * 16;
  const top = y + (h - blockHeight) / 2 + 12;
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={10}
        className={`lp-arch__box${variant ? ` lp-arch__box--${variant}` : ''}`}
      />
      <text x={cx} y={top} textAnchor="middle" className="lp-arch__title">
        {title}
      </text>
      {lines.map((line, i) => (
        <text key={line} x={cx} y={top + 17 + i * 15} textAnchor="middle" className="lp-arch__sub">
          {line}
        </text>
      ))}
    </g>
  );
};

/** A vertical edge; `both` draws arrowheads at each end (a request/response pair). */
const Edge = ({ x, y1, y2, both }: { x: number; y1: number; y2: number; both?: boolean }) => (
  <line
    x1={x}
    y1={y1}
    x2={x}
    y2={y2}
    className="lp-arch__edge"
    markerEnd="url(#lp-arch-arrow)"
    markerStart={both ? 'url(#lp-arch-arrow)' : undefined}
  />
);

/**
 * System architecture as inline SVG — no image request, themes with the page
 * (every colour is a CSS variable), and laid out portrait so it stays legible
 * at phone width.
 */
export const ArchitectureDiagram = () => (
  <figure className="lp-arch">
    <svg
      viewBox="0 0 380 478"
      role="img"
      aria-labelledby="lp-arch-title"
      aria-describedby="lp-arch-desc"
    >
      <title id="lp-arch-title">Order Bot architecture</title>
      <desc id="lp-arch-desc">
        Retailers message the distributor on WhatsApp; Meta calls the NestJS API webhook, which
        queues the work in BullMQ. The API calls the FastAPI AI service to transcribe, extract and
        match, and stores data in MongoDB, Redis and S3. Staff approve orders in the React review
        queue. A connector on the distributor's PC polls the API for approved orders and posts them
        to TallyPrime on the local network.
      </desc>
      <defs>
        <marker
          id="lp-arch-arrow"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="7"
          markerHeight="7"
          orient="auto-start-reverse"
        >
          <path d="M0 0 L10 5 L0 10 z" className="lp-arch__head" />
        </marker>
      </defs>

      {/* distributor's premises */}
      <rect x={252} y={250} width={127} height={226} rx={12} className="lp-arch__zone" />
      <text x={315} y={468} textAnchor="middle" className="lp-arch__zone-label">
        Distributor’s PC
      </text>

      <Box x={2} y={4} w={178} h={56} title="Retailer on WhatsApp" lines={['voice note or text']} />
      <Box x={200} y={4} w={178} h={56} title="Review queue" lines={['React web app · staff']} />

      <Box
        x={2}
        y={120}
        w={376}
        h={84}
        variant="core"
        title="API — NestJS"
        lines={[
          'webhook → BullMQ queue → order state machine',
          'JWT auth · audit log · per-distributor data',
        ]}
      />

      <Box
        x={2}
        y={264}
        w={116}
        h={64}
        title="AI service"
        lines={['FastAPI · Whisper', 'LLM · rapidfuzz']}
      />
      <Box x={128} y={264} w={118} h={64} title="Data" lines={['MongoDB · Redis', 'MinIO / S3']} />
      <Box
        x={260}
        y={264}
        w={112}
        h={64}
        title="Connector"
        lines={['outbound only', 'disk outbox']}
      />

      <Box
        x={2}
        y={384}
        w={241}
        h={56}
        variant="note"
        title="Approval gate"
        lines={['only staff can approve an order']}
      />
      <Box x={260} y={384} w={112} h={56} title="TallyPrime" lines={['local network']} />

      <Edge x={91} y1={60} y2={120} both />
      <text x={99} y={94} className="lp-arch__label">
        webhook in, replies out
      </text>
      <Edge x={289} y1={60} y2={120} both />
      <text x={297} y={94} className="lp-arch__label">
        HTTPS + JWT
      </text>

      <Edge x={60} y1={204} y2={264} both />
      <Edge x={187} y1={204} y2={264} both />
      <Edge x={316} y1={264} y2={204} />
      <text x={324} y={238} className="lp-arch__label">
        polls
      </text>

      <Edge x={316} y1={328} y2={384} />
      <text x={308} y={360} textAnchor="end" className="lp-arch__label">
        Tally XML
      </text>
    </svg>
    <figcaption>
      The AI service reads the stock list from MongoDB directly (read-only). Retailer confirmations
      go back out through the WhatsApp Cloud API.
    </figcaption>
  </figure>
);
